import { Component, Input, inject, OnInit } from '@angular/core';
import { NavigationEnd, Event as NavigationEvent, Router } from '@angular/router';
import { AccountService, CompanySwitcherComponent } from '@visiativ/digital-account-front';
import { SwitcherComponent } from '@visiativ/digital-application-switcher-front';
import { BookmarkListComponent } from '@visiativ/digital-bookmark-front';
import { BreakpointObserverService, StoreService } from '@visiativ/digital-components-kit';
import { NotificationBellComponent } from '@visiativ/notification-center-front';
import { environment } from '../../../../environments/environment';
import { IconMenuComponent } from '../icon-menu/icon-menu.component';
import { LanguageSwitcherComponent } from '../lang-switcher/lang-switcher.component';

@Component({
	selector: 'app-portal-contextual-heading',
	templateUrl: './portal-contextual-heading.component.html',
	styleUrls: ['./portal-contextual-heading.component.scss'],
	standalone: true,
	imports: [
		LanguageSwitcherComponent,
		NotificationBellComponent,
		CompanySwitcherComponent,
		BookmarkListComponent,
		SwitcherComponent,
		IconMenuComponent,
	],
})
export class PortalContextualHeadingComponent implements OnInit {
	private readonly router = inject(Router);
	public accountService = inject(AccountService);
	public storeService = inject(StoreService);
	public breakpointObserverService = inject(BreakpointObserverService);
	@Input() contextualHeading: { routing: string; title: string }[] = [];
	public currentContextualHeading = '';
	public isMenu = false;
	public isMobile = false;
	protected readonly environment = environment;
	private isInitialize = false;
	private isNotFound = false;

	ngOnInit(): void {
		this.getCurrentHeading();
		this.updateContextualHeadingFromCurrentUrl();

		this.breakpointObserverService.screenSizeSubject.subscribe(size => {
			this.isMobile = size === 'XSmall' || size === 'Small' || size === 'HandsetLandscape';
		});
	}

	private updateContextualHeadingFromCurrentUrl(): void {
		const currentUrl = this.router.url;
		const isNotFound = currentUrl === '/page-not-found';

		this.updateContextualHeading(
			{ url: currentUrl, urlAfterRedirects: currentUrl } as NavigationEnd,
			isNotFound,
		);
	}

	private getCurrentHeading(): void {
		this.router.events.subscribe((event: NavigationEvent) => {
			if (event instanceof NavigationEnd) {
				this.isMenu = '/menu' === event.url;
				this.isNotFound = '/page-not-found' === event.urlAfterRedirects;
				this.updateContextualHeading(event, this.isNotFound);
			}
		});
	}

	private updateContextualHeading(event: NavigationEnd, isNotFound: boolean): void {
		const targetUrl = isNotFound ? event.urlAfterRedirects : event.url;
		let matchingHeading: { title: string };

		switch (true) {
			// Cases for helpdesk URLs
			case targetUrl.startsWith('/helpdesk'):
			case targetUrl.startsWith('/helpdesk/support'):
				matchingHeading = this.contextualHeading.find(
					heading => heading.routing === '/helpdesk/tickets',
				);
				break;

			// Cases for flexo projects URLs
			case targetUrl.startsWith('/projects/flexo'):
				matchingHeading = this.contextualHeading.find(
					heading => heading.routing === '/projects/flexo',
				);
				break;

			// Case for projects URLs
			case targetUrl.startsWith('/projects'):
				matchingHeading = this.contextualHeading.find(heading => heading.routing === '/projects');
				break;

			// Case for licences URLs
			case targetUrl.startsWith('/licences/support'):
				matchingHeading = this.contextualHeading.find(heading => heading.routing === '/licences');
				break;

			// Case for contracts URLs
			case targetUrl.startsWith('/contracts/support'):
				matchingHeading = this.contextualHeading.find(heading => heading.routing === '/contracts');
				break;

			// Case for orders URLs
			case targetUrl.startsWith('/orders'):
			case targetUrl.startsWith('/orders/support'):
				matchingHeading = this.contextualHeading.find(heading => heading.routing === '/orders');
				break;

			// Case for training calendar URL
			case targetUrl.startsWith('/training/calendar'):
				matchingHeading = this.contextualHeading.find(
					heading => heading.routing === '/training/calendar',
				);
				break;

			// Case for training URLs
			case targetUrl.startsWith('/training'):
			case targetUrl.startsWith('/training/catalog'):
				matchingHeading = this.contextualHeading.find(
					heading => heading.routing === '/training/catalog',
				);
				break;

			// Case for My Session URLs
			case targetUrl.startsWith('/my-sessions'):
				matchingHeading = this.contextualHeading.find(
					heading => heading.routing === '/my-sessions',
				);
				break;

			// Case for Users URLs
			case targetUrl.startsWith('/users'):
				matchingHeading = this.contextualHeading.find(heading => heading.routing === '/users');
				break;

			// Case for Download Center URLs
			case targetUrl.startsWith('/download-center'):
				matchingHeading = this.contextualHeading.find(
					heading => heading.routing === '/download-center',
				);
				break;

			default:
				matchingHeading = this.contextualHeading.find(heading => heading.routing === targetUrl);
				break;
		}

		if (matchingHeading) {
			this.currentContextualHeading = matchingHeading.title;
		}
	}
}
